import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define types for our category data
export type Category = {
  id: number;
  name: string;
  parentId: number | null;
  children?: Category[];
};

// Define the context shape
interface CategoryContextType {
  selectedCategories: number[];
  setSelectedCategories: React.Dispatch<React.SetStateAction<number[]>>;
  isFilterActive: boolean;
  setIsFilterActive: React.Dispatch<React.SetStateAction<boolean>>;
  updateUrlWithCategories: (categoryIds: number[]) => void;
}

// Create the context with default values
const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

// Provider component that wraps the app
export const CategoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [isFilterActive, setIsFilterActive] = useState<boolean>(false);

  // Initialize from URL on component mount and when URL changes
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const categoryIdsParam = urlParams.get('categoryIds');
      
      if (categoryIdsParam) {
        const ids = categoryIdsParam.split(',').map(Number).filter(id => !isNaN(id));
        setSelectedCategories(ids);
        setIsFilterActive(true);
      } else {
        setSelectedCategories([]);
        setIsFilterActive(false);
      }
    };

    // Initial load
    handlePopState();
    
    // Listen for URL changes (back/forward buttons)
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Update URL with category IDs
  const updateUrlWithCategories = (categoryIds: number[]) => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (categoryIds.length > 0) {
      urlParams.set('categoryIds', categoryIds.join(','));
    } else {
      urlParams.delete('categoryIds');
    }
    
    // Update the browser URL without reloading
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
  };

  return (
    <CategoryContext.Provider value={{
      selectedCategories,
      setSelectedCategories,
      isFilterActive,
      setIsFilterActive,
      updateUrlWithCategories
    }}>
      {children}
    </CategoryContext.Provider>
  );
};

// Custom hook to use the context
export const useCategoryContext = () => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategoryContext must be used within a CategoryProvider');
  }
  return context;
};